import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle,
  List,
  X,
} from "@phosphor-icons/react";
import { getOtherSubdomains } from "@/subdomains/configs";
import AutoScrollRow from "@/subdomains/components/AutoScrollRow";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SubdomainSite({ config }) {
  const { theme, icon: Icon } = config;
  const others = getOtherSubdomains(config.slug);

  return (
    <div
      data-testid={`subdomain-site-${config.slug}`}
      className="min-h-screen bg-[#FFFBEB] font-sans"
    >
      <SubdomainHeader config={config} Icon={Icon} theme={theme} />
      <SubdomainHero config={config} theme={theme} Icon={Icon} />
      <SubdomainAbout config={config} theme={theme} />
      <SubdomainModules config={config} theme={theme} />
      <SubdomainSolutions config={config} theme={theme} />
      <SubdomainWhyChoose config={config} theme={theme} />
      <SubdomainCta config={config} theme={theme} />
      <SubdomainContact config={config} theme={theme} />
      <SubdomainFooter config={config} theme={theme} Icon={Icon} others={others} />
    </div>
  );
}

function SubdomainHeader({ config, Icon, theme }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navItems = [
    { label: "Home", href: "#home" },
    { label: "Solutions", href: "#solutions" },
    { label: "Modules", href: "#modules" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <header
      data-testid="subdomain-header"
      className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-zinc-100"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        <a href="#home" className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl ${theme.primary} flex items-center justify-center`}>
            <Icon size={20} weight="fill" className="text-white" />
          </div>
          <span className="text-lg font-bold text-zinc-900 tracking-tight">{config.name}</span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className={`text-sm font-medium transition-colors ${
                item.label === "Home" ? theme.primaryText : "text-zinc-600 hover:text-zinc-900"
              }`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#contact"
            className={`hidden sm:inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-white ${theme.primary} ${theme.primaryHover} transition-colors`}
          >
            Request Demo
          </a>
          <button
            type="button"
            className="lg:hidden p-2 text-zinc-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-zinc-100 bg-white px-6 py-4 space-y-3">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="block text-sm font-medium text-zinc-700 py-2"
            >
              {item.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setMobileOpen(false)}
            className={`inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-white ${theme.primary}`}
          >
            Request Demo
          </a>
        </div>
      )}
    </header>
  );
}

function SubdomainHero({ config, theme, Icon }) {
  const { hero } = config;

  return (
    <section id="home" data-testid="subdomain-hero" className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={hero.image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/60" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-32">
        <div className="max-w-2xl">
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-medium mb-8 ${theme.badge}`}>
            <Icon size={16} weight="fill" />
            {hero.badge}
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-zinc-900">
            {hero.headline.map((line, i) => (
              <span key={line} className={i === hero.highlightLine ? theme.primaryText : ""}>
                {line}
                {i < hero.headline.length - 1 && <br />}
              </span>
            ))}
          </h1>

          <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-xl">{hero.description}</p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#contact"
              className={`inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-white ${theme.primary} ${theme.primaryHover} transition-colors shadow-lg`}
            >
              {hero.ctaPrimary}
              <ArrowRight size={16} weight="bold" />
            </a>
            <a
              href="#modules"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold text-zinc-800 bg-white border border-zinc-200 hover:border-zinc-400 transition-colors"
            >
              {hero.ctaSecondary}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SubdomainAbout({ config, theme }) {
  const { about } = config;

  return (
    <section className={`${theme.cream} py-20 lg:py-28`} data-testid="subdomain-about">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20 items-start">
          <div>
            <div className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${theme.primaryText}`}>
              {about.label}
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-zinc-900 leading-tight">
              {about.heading}
            </h2>
          </div>
          <p className="text-lg text-zinc-600 leading-relaxed lg:pt-8">{about.body}</p>
        </div>
      </div>
    </section>
  );
}

function SubdomainModules({ config, theme }) {
  const { modules } = config;

  return (
    <section id="modules" className="py-20 lg:py-28 bg-white" data-testid="subdomain-modules">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${theme.primaryText}`}>
            {modules.label}
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900">{modules.heading}</h2>
          <p className="mt-4 text-zinc-600 leading-relaxed">{modules.body}</p>
        </div>

        <AutoScrollRow speed={0.35}>
          {modules.items.map((mod) => {
            const ModIcon = mod.icon;
            return (
              <div
                key={mod.title}
                data-title={mod.title}
                className="flex-shrink-0 w-72 sm:w-80 bg-white border border-zinc-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${mod.iconBg}`}>
                  <ModIcon size={24} weight="duotone" />
                </div>
                <h3 className="text-lg font-bold text-zinc-900 mb-2">{mod.title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{mod.body}</p>
              </div>
            );
          })}
        </AutoScrollRow>
      </div>
    </section>
  );
}

function SubdomainSolutions({ config, theme }) {
  const { solutions } = config;

  return (
    <section id="solutions" className={`${theme.cream} py-20 lg:py-28`} data-testid="subdomain-solutions">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14">
          <div className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${theme.primaryText}`}>
            {solutions.label}
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900">{solutions.heading}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {solutions.items.map((item) => (
            <div
              key={item.title}
              className="bg-white rounded-3xl overflow-hidden border border-zinc-100 shadow-sm hover:shadow-lg transition-shadow"
            >
              <div className="relative h-48 overflow-hidden">
                <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                <div className={`absolute top-4 left-4 w-10 h-10 rounded-xl ${theme.primary} flex items-center justify-center`}>
                  <CheckCircle size={20} weight="fill" className="text-white" />
                </div>
              </div>
              <div className="p-8">
                <h3 className="text-xl font-bold text-zinc-900 mb-4">{item.title}</h3>
                <ul className="space-y-3">
                  {item.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-sm text-zinc-600">
                      <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${theme.primary}`} />
                      {point}
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  className={`inline-flex items-center gap-1 mt-6 text-sm font-semibold ${theme.primaryText} hover:underline`}
                >
                  Explore {item.title}
                  <ArrowRight size={14} weight="bold" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SubdomainWhyChoose({ config, theme }) {
  const { whyChoose } = config;

  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="subdomain-why-choose">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="mb-14">
          <div className={`text-xs font-bold tracking-[0.2em] uppercase mb-4 ${theme.primaryText}`}>
            {whyChoose.label}
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900">{whyChoose.heading}</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="rounded-3xl overflow-hidden shadow-xl border border-zinc-100">
            <img src={whyChoose.image} alt="Analytics dashboard" className="w-full h-auto" />
          </div>
          <div className="space-y-4">
            {whyChoose.items.map((item) => (
              <div
                key={item}
                className="flex items-start gap-4 bg-white border border-zinc-100 rounded-2xl p-5 shadow-sm"
              >
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={20} weight="fill" className="text-amber-600" />
                </div>
                <p className="text-zinc-700 font-medium leading-relaxed pt-1.5">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SubdomainCta({ config, theme }) {
  return (
    <section
      data-testid="subdomain-cta"
      className={`py-20 lg:py-28 bg-gradient-to-br ${theme.heroGradient} text-white text-center`}
    >
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight">
          Ready to Transform Your {config.shortName} Operations?
        </h2>
        <p className="mt-6 text-lg text-white/85 leading-relaxed">
          Discover how {config.name} can modernize your operations into a connected, intelligent ecosystem.
        </p>
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold bg-amber-400 text-zinc-900 hover:bg-amber-300 transition-colors shadow-lg"
          >
            Schedule a Demo
            <ArrowRight size={16} weight="bold" />
          </a>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold text-white border-2 border-white/60 hover:bg-white/10 transition-colors"
          >
            Contact Sales
          </a>
        </div>
      </div>
    </section>
  );
}

function SubdomainContact({ config, theme }) {
  const [form, setForm] = useState({
    organization: "",
    contact: "",
    email: "",
    phone: "",
    industry: config.contactIndustry,
    facility: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.contact.trim() || !form.email.trim()) {
      toast.error("Please fill in contact person and email.");
      return;
    }
    if (!EMAIL_PATTERN.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({
        organization: "",
        contact: "",
        email: "",
        phone: "",
        industry: config.contactIndustry,
        facility: "",
        message: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className={`${theme.cream} py-20 lg:py-28`} data-testid="subdomain-contact">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          <div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900 leading-tight">
              Let&apos;s Design Your {config.shortName} Solution
            </h2>
            <p className="mt-4 text-zinc-600 leading-relaxed">
              Share your requirements and our team will respond with a tailored demo and consultation.
            </p>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="subdomain-contact-form"
            className="bg-white rounded-3xl p-8 shadow-lg border border-zinc-100"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <FormField label="Organization Name" name="organization" value={form.organization} onChange={onChange} />
              <FormField label="Contact Person *" name="contact" value={form.contact} onChange={onChange} required />
              <FormField label="Email Address *" name="email" type="email" value={form.email} onChange={onChange} required />
              <FormField label="Phone Number" name="phone" value={form.phone} onChange={onChange} />
              <FormField label="Industry Type" name="industry" value={form.industry} onChange={onChange} />
              <FormField label="Facility / Site Type" name="facility" value={form.facility} onChange={onChange} />
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  className="w-full bg-zinc-50 rounded-xl px-4 py-3 text-sm text-zinc-900 border-0 focus:outline-none focus:ring-2 focus:ring-zinc-200"
                  placeholder="Tell us about your operation and goals..."
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              className={`mt-6 w-full py-3.5 rounded-full text-sm font-semibold text-white ${theme.primary} ${theme.primaryHover} transition-colors disabled:opacity-60`}
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

function FormField({ label, name, value, onChange, type = "text", required = false }) {
  return (
    <div>
      <label className="text-xs font-semibold text-zinc-500 uppercase tracking-wider block mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-zinc-50 rounded-xl px-4 py-3 text-sm text-zinc-900 border-0 focus:outline-none focus:ring-2 focus:ring-zinc-200"
      />
    </div>
  );
}

function SubdomainFooter({ config, theme, Icon, others }) {
  const year = new Date().getFullYear();

  return (
    <footer data-testid="subdomain-footer" className="bg-[#FFFBEB] border-t border-zinc-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-9 h-9 rounded-xl ${theme.primary} flex items-center justify-center`}>
                <Icon size={20} weight="fill" className="text-white" />
              </div>
              <span className="text-lg font-bold text-zinc-900">{config.name}</span>
            </div>
            <p className="text-sm text-zinc-600 leading-relaxed">{config.footerTagline}</p>
          </div>

          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Platforms</div>
            <ul className="space-y-2">
              {others.map((item) => (
                <li key={item.slug}>
                  <a
                    href={`https://${item.host}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="https://smartcampus.bizlem.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                >
                  Smart Campus
                </a>
              </li>
              <li>
                <a
                  href="https://bizlem.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                >
                  Bizlem.com
                </a>
              </li>
            </ul>
          </div>

          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Get in Touch</div>
            <p className="text-sm text-zinc-600 mb-4">Let&apos;s design your intelligent infrastructure ecosystem.</p>
            <a
              href="#contact"
              className={`inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold text-white ${theme.primary} ${theme.primaryHover} transition-colors`}
            >
              Schedule a Consultation
            </a>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-zinc-200 text-center text-sm text-zinc-500">
          © {year} {config.name}. All Rights Reserved. · Powered by{" "}
          <a href="https://bizlem.com/" className="hover:text-zinc-800 transition-colors">
            Bizlem
          </a>
        </div>
      </div>
    </footer>
  );
}
